import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  useNavigate
} from 'react-router-dom';

import './AdminMembers.scss';

export default function AdminMembers() {
  const navigate =
    useNavigate();

  const apiBaseUrl =
    useMemo(() => {
      const value =
        process.env
          .REACT_APP_API_URL ||
        '';

      return value.endsWith('/')
        ? value.slice(
            0,
            -1
          )
        : value;
    }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    members,
    setMembers
  ] = useState([]);

  const [
    search,
    setSearch
  ] = useState('');

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    error,
    setError
  ] = useState('');

  useEffect(() => {
    const fetchMembers =
      async () => {
        try {
          const response =
            await fetch(
              `${apiBaseUrl}/admin/members`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const data =
            await response.json();

          if (!response.ok) {
            throw new Error(
              data?.message ||
                'Unable to load members.'
            );
          }

          setMembers(
            data.members || []
          );
        } catch (err) {
          setError(
            err.message ||
              'Unable to load members.'
          );
        } finally {
          setLoading(false);
        }
      };

    fetchMembers();
  }, [
    apiBaseUrl,
    token
  ]);

  const filteredMembers =
    useMemo(() => {
      const value =
        search
          .trim()
          .toLowerCase();

      if (!value) {
        return members;
      }

      return members.filter(
        (member) => {
          const name =
            `${member.first_name || ''} ${member.last_name || ''}`
              .toLowerCase();

          return (
            name.includes(
              value
            ) ||
            member.email
              ?.toLowerCase()
              .includes(
                value
              ) ||
            member.city
              ?.toLowerCase()
              .includes(
                value
              )
          );
        }
      );
    }, [
      members,
      search
    ]);

  const formatDate = (
    value
  ) => {
    if (!value) {
      return '—';
    }

    return new Date(
      value
    ).toLocaleDateString(
      undefined,
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }
    );
  };

  return (
    <div className="admin-members-page">

      <section className="admin-members-heading">

        <div>
          <p>
            Membership
          </p>

          <h2>
            Members
          </h2>

          <span>
            Search and review
            member activity.
          </span>
        </div>

      </section>

      <section className="admin-members-summary">

        <div>
          <span>
            Total Members
          </span>

          <strong>
            {members.length}
          </strong>
        </div>

        <div>
          <span>
            With Rounds
          </span>

          <strong>
            {
              members.filter(
                (member) =>
                  Number(
                    member.rounds_count
                  ) > 0
              ).length
            }
          </strong>
        </div>

        <div>
          <span>
            Event Participants
          </span>

          <strong>
            {
              members.filter(
                (member) =>
                  Number(
                    member.event_count
                  ) > 0
              ).length
            }
          </strong>
        </div>

      </section>

      <section className="admin-members-search">

        <label>
          Search Members
        </label>

        <input
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          placeholder="Name, email or city..."
        />

      </section>

      {error ? (
        <div className="admin-members-error">
          {error}
        </div>
      ) : null}

      <section className="admin-members-table-card">

        <div className="admin-members-table-card__header">

          <h3>
            Member Directory
          </h3>

          <span>
            {filteredMembers.length}
            {' '}
            members
          </span>

        </div>

        {loading ? (
          <div className="admin-members-empty">
            Loading members...
          </div>
        ) : filteredMembers.length ===
          0 ? (
          <div className="admin-members-empty">
            No members found.
          </div>
        ) : (
          <div className="admin-members-table-wrapper">

            <table className="admin-members-table">

              <thead>
                <tr>
                  <th>
                    Member
                  </th>

                  <th>
                    Location
                  </th>

                  <th>
                    Joined
                  </th>

                  <th>
                    Rounds
                  </th>

                  <th>
                    Events
                  </th>

                  <th>
                    Last Round
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredMembers.map(
                  (member) => (
                    <tr
                      key={
                        member.user_id
                      }
                    >
                      <td>
                        <div className="admin-member-name">

                          <div className="admin-member-avatar">
                            {String(
                              member.first_name ||
                                member.email ||
                                '?'
                            )
                              .charAt(0)
                              .toUpperCase()}
                          </div>

                          <div>
                            <strong>
                              {member.first_name}
                              {' '}
                              {member.last_name}
                            </strong>

                            <span>
                              {member.email}
                            </span>
                          </div>

                        </div>
                      </td>

                      <td>
                        {[
                          member.city,
                          member.province
                        ]
                          .filter(
                            Boolean
                          )
                          .join(', ') ||
                          '—'}
                      </td>

                      <td>
                        {formatDate(
                          member.created_at
                        )}
                      </td>

                      <td>
                        {
                          member.rounds_count ||
                          0
                        }
                      </td>

                      <td>
                        {
                          member.event_count ||
                          0
                        }
                      </td>

                      <td>
                        {formatDate(
                          member.last_round_at
                        )}
                      </td>

                      <td>
                        <span className="admin-member-status">
                          {
                            member.account_status ||
                            'ACTIVE'
                          }
                        </span>
                      </td>

                      <td>
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/admin/members/${member.user_id}`
                            )
                          }
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>

            </table>

          </div>
        )}

      </section>

    </div>
  );
}